import { defineComponent, reactive, SetupContext, readonly, ref, onMounted } from '@vue/composition-api';
import HttpStatus from 'http-status-codes';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import CommonMaterialFilter from '@/components/common/common-material-filter.component.vue';
import { SearchConditionType, InputConditionType } from '@/components/common/common-material-filter.component';
import SystemConst from '@/shared/system-const';
import { TableHeader, MaterialList, PullDown } from '@/shared/type';
import vueRouter from '@/router/index';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                               // Windowサイズ
    schoolTypes: { id: string; name: string }[];                        // 校種一覧（タブ一覧）
    isTabLoading: boolean;                                              // タブ一覧取得のロード中: true ロード中でない: false
    searchConditions: SearchConditionType;                              // バックから取得した検索条件
    selectAllSchoolTypes: [],                                           // 校種の選択状態
    selectGrades: [],                                                   // 学年の選択状態
    selectCurriculums: [],                                              // 教科・科目の選択状態
    selectMaterialTypes: [],                                            // 教材の種類の選択状態
    keyword: null,                                                      // 検索条件のキーワード
    materialTableHeaders: TableHeader[];
    materialItems: MaterialList[];
    expanded: MaterialList[];
    sortItems: PullDown[];
    thumbnailSwitch: boolean;
    sortStatus: string;
    tab: string;
    totalCount: number;                                                 // 教材一覧の総件数
    isLoading: boolean;                                                 // テーブルがロード中: true ロード中でない: false
    isAdmin: boolean;                                                   // 管理ボタン表示フラグ
    isDisplayButton: boolean;                                           // 編集・削除ボタン表示フラグ
    tmpDisplayButton: boolean;                                          // 編集・削除ボタン状態一時保存
}

/**
 * 教材検索画面コンポーネント
 */
export default defineComponent({
    components: { CommonMaterialFilter, CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const commonRef = ref<any>(null);                                       // メッセージダイアログ
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                   // 1ページあたりの行数候補（10, 50, 100）
        const itemsPerPage = ref<number>(linesPerPage[0]);                      // 1ページあたりの行数（状態管理用、デフォルト値：50）
        const pageNation = ref<number>(1);                                      // ページ番号（状態管理用）
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;                // テーブルのデータ取得中に表示するテキスト
        let tableHeight = SystemConst.TABLE_HEIGHT.MATERIAL_DB_LIST_LOADING;    // テーブル高さ調整用変数
        let isPageLastData = false;                                             // ページの最終データフラグ

        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                         // ウィンドウサイズ
            schoolTypes: [],                                                    // 校種一覧（タブ一覧）
            isTabLoading: true,                                                 // タブ一覧取得のロード中: true ロード中でない: false
            searchConditions: {
                schoolTypes: [],
                materialTypes: []
            },                                                                  // バックから取得した検索条件
            selectAllSchoolTypes: [],                                           // 校種の選択状態
            selectGrades: [],                                                   // 学年の選択状態
            selectCurriculums: [],                                              // 教科・科目の選択状態
            selectMaterialTypes: [],                                            // 教材の種類の選択状態
            keyword: null,                                                      // 検索条件のキーワード
            materialTableHeaders: [],
            materialItems: [],
            expanded: [],
            sortItems: [
                {
                    'value': SystemConst.SORT_ORDER['DESC'],
                    'text': SystemConst.MATERIAL_PULLDOWN_MENU['DESC']
                },
                {
                    'value': SystemConst.SORT_ORDER['ASC'],
                    'text': SystemConst.MATERIAL_PULLDOWN_MENU['ASC']
                }
            ],
            thumbnailSwitch: true,
            sortStatus: SystemConst.SORT_ORDER.DESC,
            tab: '',                                                            // タブの選択状態（校種ID格納）
            totalCount: 0,                                                      // 教材一覧の総件数
            isLoading: false,                                                   // テーブルがロード中: true ロード中でない: false
            isAdmin: false,                                                     // 管理ボタン表示フラグ
            isDisplayButton: false,                                             // 編集・削除ボタン表示フラグ
            tmpDisplayButton: false                                             // 編集・削除ボタン状態一時保存
        });
        const tableHeaderClass: string[] = ['tableHeader subtitle-2'];
        const filterMaterialRef = ref<any>(null);                               // 検索条件
        const materialTableHeaders: TableHeader[] = [
            {
                text: 'タイトル',
                value: 'title',
                sortable: false,
                width: '98%',
                class: tableHeaderClass
            },
            // 行展開
            {
                text: '',
                value: 'data-table-expand',
                sortable: false,
                width: '2%',
                align: 'end',
                class: tableHeaderClass,
            }
        ];
        const router = vueRouter;                                               // ルータ
        const authenticationUser
            = ctx.root.$store.state.authenticationUser;                         // storeファイルからSAML認証で取得したパラメータを取得する
        const circularSize = SystemConst.MATERIAL_DB_LOADING_CIRCULAR.SIZE;     // 校種情報取得中のローダーサイズ
        const circularWidth = SystemConst.MATERIAL_DB_LOADING_CIRCULAR.WIDTH;   // 校種情報取得中のローダー幅

        /**
         * 初期処理
         */
        onMounted(() => {
            init();
        });
        async function init() {
            // ストアのschoolTypesTab に校種タブ情報が格納されていないことを判別する
            if (ctx.root.$store.state.materialDb.schoolTypesTab.length === 0) {
                // タブ情報がストアにない場合、タブ情報取得処理を呼び出す
                await getSchoolTypesTab();
            } else {
                // 校種タブ情報がある場合、ストアから復元する
                state.schoolTypes = ctx.root.$store.state.materialDb.schoolTypesTab;
                // 一番目の校種IDをstate.tabに設定する
                state.tab = state.schoolTypes[0].id;
            }

            // 遷移元画面URL
            const url = ctx.root.$store.getters.getBeforeDisplayPath;

            // 遷移元画面が教材詳細、教材削除、教材編集の場合、画面表示項目をストアから復元する
            if (
                url.indexOf('/material-db/detail/') !== -1 ||
                url.indexOf('/material-db/delete/') !== -1 ||
                url.indexOf('/material-db/edit/') !== -1            
            ) {
                // タブ
                state.tab = ctx.root.$store.state.materialDb.schoolTypeId;

                // ・1ページあたりの行数 ※設定値が0の場合はストア未設定と判断する
                if (ctx.root.$store.state.materialDb.itemsPerPage !== 0) {
                    itemsPerPage.value = ctx.root.$store.state.materialDb.itemsPerPage;
                }

                // ・ページ番号 ※設定値が0の場合はストア未設定と判断する
                if (ctx.root.$store.state.materialDb.pageNation !== 0) {
                    pageNation.value = ctx.root.$store.state.materialDb.pageNation;
                }

                // ・ソート順 ※設定値が空文字の場合はストア未設定と判断する
                if (ctx.root.$store.state.materialDb.sortOrder !== '') {
                    state.sortStatus = ctx.root.$store.state.materialDb.sortOrder;
                }

                // ・サムネイル表示フラグ
                state.thumbnailSwitch = ctx.root.$store.state.materialDb.isDisplayThumbnail;

                // 教材データベースの管理権限（authenticationUser.role.isMaterialDb）がtrueの場合
                if (authenticationUser.role.isMaterialDb) {
                    // 編集・削除ボタン表示フラグを復元する
                    state.isDisplayButton = ctx.root.$store.state.materialDb.isDisplayButton;
                    state.tmpDisplayButton = state.isDisplayButton;
                }
            } else {
                // 他の画面からの遷移の場合はストアをリセットする
                resetStore(true);
            }
            // 選択中のタブ情報をストアに保存する
            ctx.root.$store.commit('materialDb/setSchoolTypeId', state.tab);

            // ストアからisPageLastDataを復元
            isPageLastData = ctx.root.$store.state.materialDb.isPageLastData;
            // storeのページの最終データフラグをfalseに設定
            ctx.root.$store.commit('materialDb/setIsPageLastData', false);
            
            // isPageLastDataがtrueの場合、ページ番号を-1する
            if (isPageLastData) {
                pageNation.value -= 1;
                ctx.root.$store.commit('materialDb/setPageNation', pageNation.value);
            }

            // 表示中のローダーを解除する
            state.isTabLoading = false;

            // テーブルサイズもローダー表示中と異なるため、更新する
            tableHeight = SystemConst.TABLE_HEIGHT.MATERIAL_DB_LIST;

            // 教材DB書き込み権限取得処理呼び出し
            await getMaterialRole();

            // 教材検索 フィルタ条件取得APIで検索条件を取得する
            const response = await ctx.root.$store.dispatch('materialDb/getFilterConditions');

            // HTTPステータスコードが200以外の場合、エラー画面へ遷移して処理を終了する
            if (response.status !== HttpStatus.OK) {
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 検索条件をcommon-material-filterコンポーネントへ設定する
            state.searchConditions =
            {
                schoolTypes: response.data.schoolTypes,
                materialTypes: response.data.materialTypes
            };

            // 教材一覧取得APIを呼び出して一覧表示する
            updateList();
        }

        /**
         * 教材DB書き込み権限取得処理
         */
        async function getMaterialRole() {
            // 教材DB書き込み権限取得APIを呼び出す
            // パラメータ: なし
            const response = await ctx.root.$store.dispatch('materialDb/getMaterialRole');

            // 通信結果判別
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                // エラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // ストアの認証ユーザー情報取得
            const loginUser = ctx.root.$store.state.authenticationUser;

            // 認証ユーザー情報の教材DB書き込み権限に取得した権限を設定する
            loginUser.role.isMaterialDb = response.data.isMaterialDb;

            // 認証ユーザー情報をストアに保存
            ctx.root.$store.commit('setAuthenticationUser', loginUser);

            // storeファイルから教材DB書き込み権限を取得し、state.isAdminに設定する
            state.isAdmin = ctx.root.$store.state.authenticationUser.role.isMaterialDb;
        }

        /**
         * 校種設定タブ情報取得処理
         */
        async function getSchoolTypesTab() {
            // 校種タブ取得用APIを呼び出す
            // パラメータ: なし
            const response = await ctx.root.$store.dispatch('materialDb/getSchoolTypesTab');

            // 通信結果判別
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                // エラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // レスポンスデータをstate.schoolTypes に設定する
            state.schoolTypes = response.data.schoolTypes;
            // 一番目の校種IDをstate.tabに設定する
            state.tab = state.schoolTypes[0].id;

            // 取得した校種タブの情報をストアのschoolTypesTab に保存する
            ctx.root.$store.commit('materialDb/setSchoolTypesTab', state.schoolTypes);
        }

        /**
         * 初期表示
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-database-search');

            // mainTitleを「教材検索」に変更
            ctx.root.$store.commit('setMainTitle', '教材検索');

            // ヘッダー設定
            state.materialTableHeaders = materialTableHeaders;
        }

        /**
         * タブクリックイベント
         */
        function onClickTab(id: string) {
            // 一覧と検索件数、ページ番号のリセット
            pageNation.value = 1;
            state.totalCount = 0;
            state.materialItems = [];

            // ストアに保持してある値をリセットする
            resetStore(false);

            // 選択した校種をストアへ保存する
            ctx.root.$store.commit('materialDb/setSchoolTypeId', id);

            // フィルタの条件があれば追加する
            filterMaterialRef.value.forEach((tab: any) => {
                // 選択されたIDのタブにフィルタ条件が設定されていたらストアに設定する
                if (tab.$options.propsData.schoolTypeId === id) {
                    // 校種
                    if (tab.state.selectAllSchoolTypes) {
                        ctx.root.$store.commit('materialDb/setAllSchoolTypes', tab.state.selectAllSchoolTypes);
                    }
                    // 学年
                    if (tab.state.selectGrades) {
                        ctx.root.$store.commit('materialDb/setGrades', tab.state.selectGrades);
                    }
                    // 教科・科目
                    if (tab.state.selectCurriculums) {
                        ctx.root.$store.commit('materialDb/setCurriculums', tab.state.selectCurriculums);
                    }
                    // 教材の種類
                    if (tab.state.selectMaterialTypes) {
                        ctx.root.$store.commit('materialDb/setMaterialTypes', tab.state.selectMaterialTypes);
                    }
                    // キーワード
                    if (tab.state.keyword) {
                        ctx.root.$store.commit('materialDb/setKeyword', tab.state.keyword);
                    }
                }
            });

            // 教材一覧取得APIを呼び出して一覧表示する
            updateList();
        }

        /**
         * ストアに保存した情報をリセットする
         * @param isChangePage 画面初期表示時の呼び出し:true それ以外の呼び出し:false
         */
        function resetStore(isChangePage: boolean) {
            // ・校種タブID
            ctx.root.$store.commit('materialDb/setSchoolTypeId', '');
            // ・校種
            ctx.root.$store.commit('materialDb/setAllSchoolTypes', []);
            // ・学年
            ctx.root.$store.commit('materialDb/setGrades', []);
            // ・教科・科目
            ctx.root.$store.commit('materialDb/setCurriculums', []);
            // ・教材の種類
            ctx.root.$store.commit('materialDb/setMaterialTypes', []);
            // ・キーワード
            ctx.root.$store.commit('materialDb/setKeyword', '');            
            // ・ページ番号
            ctx.root.$store.commit('materialDb/setPageNation', 0);

            // 画面遷移時のストアリセット処理の場合のみ初期化する要素
            // 校種タブの変更ではリセットしない要素
            if(isChangePage) {
                // ・1ページ毎の行数
                ctx.root.$store.commit('materialDb/setItemsPerPage', 0);
                // ソート順
                ctx.root.$store.commit('materialDb/setSortOrder', '');
                // 編集・削除ボタン表示フラグ
                ctx.root.$store.commit('materialDb/setIsDisplayButton', false);
                // ・サムネイル表示フラグ
                ctx.root.$store.commit('materialDb/setIsDisplayThumbnail', true);
            }
        }

        /**
         * ストアから値を取得して、画面遷移前の検索条件を復元する
         * 教材検索コンポーネントの初期表示時に呼ばれる
         */
        function restoreFilter() {
            filterMaterialRef.value.forEach((tab: any) => {
                // 選択されたIDのタブにフィルタ条件が設定されていたら優先して設定する
                if (tab.$options.propsData.schoolTypeId === ctx.root.$store.state.materialDb.schoolTypeId) {
                    // 校種
                    tab.state.selectAllSchoolTypes = ctx.root.$store.state.materialDb.allSchoolTypes;
                    // 値が入っている場合は検索フィルタを展開する
                    if (tab.state.selectAllSchoolTypes.length !== 0) {
                        tab.state.showAllSchoolTypes = true;
                    }
                    // 学年
                    tab.state.selectGrades = ctx.root.$store.state.materialDb.grades;
                    // 値が入っている場合は検索フィルタを展開する
                    if (tab.state.selectGrades.length !== 0) {
                        tab.state.showGrade = true;
                    }
                    // 教科・科目
                    tab.state.selectCurriculums = ctx.root.$store.state.materialDb.curriculums;
                    // 値が入っている場合は検索フィルタを展開する
                    if (tab.state.selectCurriculums.length !== 0) {
                        tab.state.showSubject = true;
                    }
                    // 教材の種類
                    tab.state.selectMaterialTypes = ctx.root.$store.state.materialDb.materialTypes;
                    // 値が入っている場合は検索フィルタを展開する
                    if (tab.state.selectMaterialTypes.length !== 0) {
                        tab.state.showMaterialType = true;
                    }
                    // キーワード
                    tab.state.keyword = ctx.root.$store.state.materialDb.keyword;
                    // 値が入っている場合は検索フィルタを展開する
                    if (tab.state.keyword) {
                        tab.state.showKeyword = true;
                    }
                }
            });
        }

        /**
         * 検索ボタン押下によるイベント
         * 
         * @param inputConditions 入力された検索条件
         */
        function updateFilter(inputConditions: InputConditionType) {
            // 以下の検索条件をストアへ保存する
            // ・校種
            ctx.root.$store.commit('materialDb/setAllSchoolTypes', inputConditions.allSchoolTypes);
            // ・学年
            ctx.root.$store.commit('materialDb/setGrades', inputConditions.grades);
            // ・教科・科目
            ctx.root.$store.commit('materialDb/setCurriculums', inputConditions.curriculums);
            // ・教材の種類
            ctx.root.$store.commit('materialDb/setMaterialTypes', inputConditions.materialTypes);
            // ・キーワード
            ctx.root.$store.commit('materialDb/setKeyword', inputConditions.keyword);
            // ・1ページ毎の行数
            ctx.root.$store.commit('materialDb/setItemsPerPage', itemsPerPage.value);
            // ・ページ番号（1ページ目に戻る）
            pageNation.value = 1;
            ctx.root.$store.commit('materialDb/setPageNation', 1);
            // ・ソート順
            ctx.root.$store.commit('materialDb/setSortOrder', state.sortStatus);
            // ・サムネイル表示フラグ
            ctx.root.$store.commit('materialDb/setIsDisplayThumbnail', state.thumbnailSwitch);

            // 教材一覧取得APIを呼び出して一覧表示する
            updateList();
        }

        /**
         * ソートプルダウン選択イベント
         */
        function sort(sort: string) {
            // 選択中のソート条件からソート順（asc or desc）をストアへ保存する
            if (sort === SystemConst.SORT_ORDER['ASC']) {
                // 昇順 = 古い順
                state.sortStatus = SystemConst.SORT_ORDER.ASC;
            } else {
                // 降順 = 新しい順
                state.sortStatus = SystemConst.SORT_ORDER.DESC;
            }
            ctx.root.$store.commit('materialDb/setSortOrder', state.sortStatus);

            // ページ番号をリセットする
            pageNation.value = 1;
            ctx.root.$store.commit('materialDb/setPageNation', 1);

            // 教材一覧取得APIを呼び出して一覧表示する
            updateList();
        }

        /**
         * サムネイル表示切り替えによるイベント
         */
        function changeThumbnailSwitch() {
            // サムネイル表示フラグをストアに保存する
            ctx.root.$store.commit('materialDb/setIsDisplayThumbnail', state.thumbnailSwitch);

            // 教材一覧取得APIを呼び出して一覧表示する
            updateList();
        }

        /**
         * ページ切替イベント
         * @param page ページ番号
         */
        function updatePage(page: number) {
            // 変更されていなかった場合
            if (pageNation.value === page) {
                // 処理を終了する
                return;
            }

            // 以下の検索条件をストアへ保存する
            // ・ページ番号
            pageNation.value = page;
            ctx.root.$store.commit('materialDb/setPageNation', page);

            // 教材一覧取得APIを呼び出して一覧を更新する
            updateList();
        }

        /**
         * 1ページ当たりの行数更新イベント
         * @param perPage 選択された1ページ毎の行数
         */
         function updateItemsPerPage(perPage: number) {
            // 変更されていなかった場合
            if (itemsPerPage.value === perPage) {
                // 処理を終了する
                return;
            }

            // 以下の検索条件をストアへ保存する
            // ・1ページ毎の行数
            itemsPerPage.value = perPage;
            ctx.root.$store.commit('materialDb/setItemsPerPage', perPage);

            // 教材一覧取得APIを呼び出して一覧を更新する
            updateList();
        }

        /**
         * 教材一覧検索処理
         */
        async function updateList() {
            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // ページの最終データフラグをfalseに設定する
            isPageLastData = false;

            // 検索条件がストアに設定されている場合は引数に設定する
            // ・校種タブID
            const schoolTypeId = ctx.root.$store.state.materialDb.schoolTypeId;
            // ・校種
            const allSchoolTypes = ctx.root.$store.state.materialDb.allSchoolTypes;
            // ・学年
            const grades = ctx.root.$store.state.materialDb.grades;
            // ・教科・科目
            const curriculums = ctx.root.$store.state.materialDb.curriculums;
            // ・教材の種類
            const materialTypes = ctx.root.$store.state.materialDb.materialTypes;
            // ・キーワード
            const keyword = ctx.root.$store.state.materialDb.keyword;

            // 教材一覧取得APIでデータを取得する
            // GET /api/material-db
            // パラメータ：ページ番号
            //            1ページあたりの行数
            //            ソート順
            //            校種タブID
            //            校種
            //            学年
            //            教科・科目
            //            教材の種類
            //            キーワード
            //            教材管理モードフラグ
            const response = await ctx.root.$store.dispatch('materialDb/getMaterial',
                {
                    page: pageNation.value,
                    itemsPerPage: itemsPerPage.value,
                    sortOrder: state.sortStatus,
                    schoolTypeId: schoolTypeId,
                    allSchoolTypes: allSchoolTypes,
                    grades: grades,
                    curriculums: curriculums,
                    materialTypes: materialTypes,
                    keyword: keyword,
                    isMaterialAdmin: Number(state.tmpDisplayButton),
                    isDisplayThumbnail: Number(state.thumbnailSwitch)
                }
            );

            // API通信のレスポンスが正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 詳細を展開表示する
            state.expanded = response.data.materialLists;

            // 取得した教材一覧をstate.materialItemsに設定する
            state.materialItems = response.data.materialLists;

            // 検索件数を設定する
            state.totalCount = response.data.totalCount;

            // 取得件数が1件かつ総データ件数が1件より多い場合、isPageLastDataをtrueにする
            if (state.materialItems.length === 1 && state.totalCount > 1) {
                isPageLastData = true;
            }

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;
        }

        /**
         * 管理者ボタン押下
         */
        async function onClickAdmin(isDisplay: boolean) {
            // 編集・削除ボタン表示フラグを切り替える
            state.tmpDisplayButton = isDisplay;

            // 管理者権限をストアへ保存する
            ctx.root.$store.commit('materialDb/setIsDisplayButton', isDisplay);

            // ページ番号をリセットする（1ページ目に戻る）
            pageNation.value = 1;
            ctx.root.$store.commit('materialDb/setPageNation', 1);

            // 教材一覧取得APIを呼び出して一覧を更新する
            await updateList();

            // 編集・削除ボタン表示フラグを切り替える
            state.isDisplayButton = isDisplay;
        }

        /**
         * 編集アイコン押下
         */
        function onClickEdit(id: string) {
            // 教材詳細画面に遷移する
            // material-db/edit/教材ID
            router.push('/material-db/edit/' + id);
        }

        /**
         * 削除アイコン押下
         */
        function onClickDelete(id: string) {
            // isPageLastDataをstoreに保存する
            ctx.root.$store.commit('materialDb/setIsPageLastData', isPageLastData);

            // 教材詳細画面に遷移する
            // material-db/delete/教材ID
            router.push('/material-db/delete/' + id);
        }

        /**
         * タイトルクリックイベント
         */
        function navigationDetail(id: string) {
            // 教材詳細画面に遷移する
            // material-db/detail/教材ID
            router.push('/material-db/detail/' + id);
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };

            // 教材検索条件コンポーネントのサイズも変更（タブにより複数あるコンポーネントに対し変更する）
            filterMaterialRef.value.forEach((x: any) => x.resize(state.windowSize));
        }

        return {
            commonRef,
            linesPerPage,
            itemsPerPage,
            pageNation,
            tableLoadingText,
            filterMaterialRef,
            tableHeight,
            circularSize,
            circularWidth,
            state: readonly(state),
            onClickTab,
            restoreFilter,
            updateFilter,
            sort,
            changeThumbnailSwitch,
            updatePage,
            updateItemsPerPage,
            onClickAdmin,
            onClickEdit,
            onClickDelete,
            navigationDetail,
            onResize
        };
    }
});
